import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import SelectManagerList, { type ICounselor } from '@/components/chat/SelectManagerList'
import MainTopBar from '@/components/common/bar/MainTopBar'
import WideButton from '@/components/common/button/WideButton'
import { useUserStore } from '@/store/userStore'
import { useUpdateUserConsultStyleMutation } from '@/hooks/queries/user/mutation'
import type { ConsultStyle } from '@/api/model/userType'
import { useUserConsultStyleQuery } from '@/hooks/queries/user'

const counselors: ICounselor[] = [
  {
    id: 'caring',
    name: '다정이',
    character: '🐝',
    description: '언제나 네 편이 되어줄게!\n걱정말고 나한테 다 말해봐!',
    quote: '온화한 미소로 그 어떤 투정도 다 받아주는 한없이 착한 큐비! 혼자 공부 왔던 이야기를 마음껏 말해봐시다!',
    details: '혹시 마음에 안 들면, 다른 상담사로 고를 수 있어. 모든 상담사는 너에게 진심이야. 편하게 골라봐!',
    color: 'bg-yellow-400'
  },
  {
    id: 'direct',
    name: '직답이',
    character: '🐝',
    description: '솔직하고 명확한 조언을 해줄게!',
    quote: '직설적이지만 따뜻한 마음을 가진 상담사예요.',
    details: '때로는 직접적인 조언이 필요할 때가 있어요. 함께 문제를 해결해봐요!',
    color: 'bg-gray-400'
  },
  {
    id: 'passionate',
    name: '열정이',
    character: '🐝',
    description: '에너지 넘치는 상담으로 힘을 줄게!',
    quote: '열정적이고 활기찬 상담사예요.',
    details: '긍정적인 에너지로 함께 성장해나가요!',
    color: 'bg-orange-400'
  }
]

const counselorStyle = {
  caring: 'WEAK',
  direct: 'MODERATE',
  passionate: 'STRONG'
}

const translateCounselorStyle = {
  WEAK: 'caring',
  MODERATE: 'direct',
  STRONG: 'passionate'
}

const isProgressingCounselor = false

const MySelectCounselor = () => {
  const userStore = useUserStore()
  const navigate = useNavigate()
  const user_id = userStore.userInfo?.user_id ?? 0
  const { data: userConsultStyle } = useUserConsultStyleQuery(user_id)
  const { mutate: updateUserConsultStyle } = useUpdateUserConsultStyleMutation({
    onSuccess: async () => {
      navigate(-1)
    }
  })

  useEffect(() => {
    if (userConsultStyle?.data.consult_style === null || userConsultStyle?.data.consult_style === undefined) {
      setSelectedCounselor('caring')
    } else {
      setSelectedCounselor(
        translateCounselorStyle[userConsultStyle.data.consult_style] as keyof typeof translateCounselorStyle
      )
    }
  }, [userConsultStyle?.data.consult_style])
  const [selectedCounselor, setSelectedCounselor] = useState<string>('direct')

  const handleCounselorSelect = (counselor: ICounselor) => {
    setSelectedCounselor(counselor.id)
  }

  const handleStartCounseling = () => {
    updateUserConsultStyle({
      user_id: userStore.userInfo?.user_id ?? 0,
      body: {
        style: counselorStyle[selectedCounselor as keyof typeof counselorStyle] as ConsultStyle
      }
    })
  }

  return (
    <div className='min-h-screen bg-gradient-to-r from-amber-400 to-yellow-300 flex flex-col'>
      {/* 헤더 */}
      <MainTopBar
        hasBackButton={true}
        handleClickBackButton={() => navigate(-1)}
        leftSide={
          <div className='flex items-center'>
            <h1 className='text-header-title font-semibold text-text-header'>나의 상담사</h1>
          </div>
        }
      />

      {/* 메인 콘텐츠 영역 */}
      <div className='flex-1 px-4 flex flex-col'>
        <div className='h-[12vh]'></div>
        {/* 상담사 목록 */}
        <SelectManagerList
          counselors={counselors}
          selectedCounselor={counselors.find(counselor => counselor.id === selectedCounselor) ?? counselors[0]}
          handleCounselorSelect={handleCounselorSelect}
        />

        {/* 선택된 상담사 정보 카드 */}
        <div className='bg-white rounded-2xl shadow-lg overflow-hidden mb-4'>
          <div className='p-6'>
            <h3 className='text-xl font-bold text-gray-800 text-center mb-4'>
              {counselors.find(counselor => counselor.id === selectedCounselor)?.name}
            </h3>

            <div className='bg-amber-50 rounded-2xl p-4 mb-4 border border-amber-200'>
              <p className='text-sm font-medium text-amber-900 text-center leading-relaxed whitespace-pre-line'>
                "{counselors.find(counselor => counselor.id === selectedCounselor)?.description}"
              </p>
            </div>

            <p className='text-sm text-gray-600 leading-relaxed text-center'>
              {counselors.find(counselor => counselor.id === selectedCounselor)?.quote}
            </p>
          </div>
        </div>

        {/* 설명 문구 */}
        <div className='flex-1 flex items-end'>
          <p className='text-xs text-amber-900 leading-relaxed text-center px-4 mb-4 w-full whitespace-pre-line'>
            {isProgressingCounselor
              ? '진행중인 상담이 있어서 지금은 상담사를 변경할 수 없어요!\n상담을 완료한 후 다시 시도해주세요.'
              : '상담사를 너무 자주 변경하면 상담사의 기억력이 저하될 수 있어요.'}
          </p>
        </div>

        {!isProgressingCounselor && (
          <div className='pb-6'>
            <WideButton handleClick={handleStartCounseling} buttonText='저장' />
          </div>
        )}
      </div>
    </div>
  )
}

export default MySelectCounselor
